import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

export type GlassPicklistOption<T extends string = string> = {
  value: T;
  label: string;
};

type GlassPicklistProps<T extends string = string> = {
  value: T;
  options: ReadonlyArray<GlassPicklistOption<T>>;
  onChange: (nextValue: T) => void;
  ariaLabel: string;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyLabel?: string;
};

const Wrap = styled.div`
  position: relative;
`;

const TriggerButton = styled.button<{ $open: boolean }>`
  width: 100%;
  min-height: 44px;
  border-radius: 12px;
  border: 1px solid ${({ $open }) => ($open ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.9)')};
  background: transparent;
  color: rgba(248, 250, 255, 0.96);
  padding: 0 36px 0 12px;
  font-size: 0.92rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  transition: border-color 140ms ease;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(255, 255, 255, 1);
  }
`;

const TriggerChevron = styled.span<{ $open: boolean }>`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%) rotate(${({ $open }) => ($open ? 180 : 0)}deg);
  color: rgba(248, 250, 255, 0.92);
  font-size: 0.78rem;
  line-height: 1;
  pointer-events: none;
  transition: transform 140ms ease;
`;

const Menu = styled.div<{
  $top: number;
  $left: number;
  $width: number;
  $openUp: boolean;
}>`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  transform: ${({ $openUp }) => ($openUp ? 'translateY(-100%)' : 'none')};
  z-index: 120;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(17, 22, 33, 0.75);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.35);
  padding: 6px;
  display: grid;
  gap: 4px;
`;

const MenuViewport = styled.div<{ $maxHeight: number }>`
  max-height: ${({ $maxHeight }) => `${$maxHeight}px`};
  overflow-y: auto;
  overflow-x: hidden;
  display: grid;
  gap: 4px;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.22);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.34);
  }
`;

const MenuOption = styled.button<{ $selected: boolean }>`
  width: 100%;
  border: 1px solid transparent;
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? 'rgba(255, 255, 255, 0.14)' : 'transparent')};
  color: rgba(248, 250, 255, 0.96);
  padding: 7px 10px;
  text-align: left;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(255, 255, 255, 0.45);
  }
`;

const MenuSearch = styled.input`
  width: 100%;
  min-height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(248, 250, 255, 0.96);
  padding: 0 10px;
  font-size: 0.82rem;
  font-weight: 600;

  &::placeholder {
    color: rgba(248, 250, 255, 0.62);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(255, 255, 255, 0.72);
  }
`;

const EmptyText = styled.div`
  border-radius: 8px;
  border: 1px dashed rgba(255, 255, 255, 0.28);
  padding: 8px 10px;
  color: rgba(248, 250, 255, 0.78);
  font-size: 0.8rem;
  font-weight: 600;
`;

export default function GlassPicklist<T extends string = string>({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder = '',
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  emptyLabel = 'No options found.',
}: GlassPicklistProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 280,
    openUp: false,
    maxHeight: 320,
  });
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const selectedLabel = useMemo(
    () => options.find((option) => option.value === value)?.label ?? '',
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((option) => option.label.toLowerCase().includes(normalized));
  }, [options, query, searchable]);

  const syncMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const estimatedMenuHeight = Math.min(
      420,
      (searchable ? 48 : 0) + options.length * 38 + 22,
    );
    const spaceAbove = rect.top - 12;
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const openUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
    const availableHeight = openUp ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(120, Math.min(availableHeight, 420));

    setMenuPosition({
      top: openUp ? rect.top - 8 : rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      openUp,
      maxHeight,
    });
  }, [options.length, searchable]);

  const viewportMaxHeight = Math.max(
    72,
    menuPosition.maxHeight - (searchable ? 46 : 0),
  );

  useEffect(() => {
    if (!open) return;
    syncMenuPosition();

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const clickedPicker = Boolean(target) && Boolean(pickerRef.current?.contains(target));
      const clickedMenu = Boolean(target) && Boolean(menuRef.current?.contains(target));
      if (!clickedPicker && !clickedMenu) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const handleViewportShift = () => {
      syncMenuPosition();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportShift);
    window.addEventListener('scroll', handleViewportShift, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportShift);
      window.removeEventListener('scroll', handleViewportShift, true);
    };
  }, [open, syncMenuPosition]);

  return (
    <Wrap ref={pickerRef}>
      <TriggerButton
        ref={triggerRef}
        type="button"
        $open={open}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => {
          if (!open) {
            syncMenuPosition();
            setQuery('');
          }
          setOpen((prev) => !prev);
        }}
      >
        {selectedLabel || placeholder}
      </TriggerButton>
      <TriggerChevron $open={open}>v</TriggerChevron>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <Menu
              ref={menuRef}
              role="listbox"
              aria-label={ariaLabel}
              $top={menuPosition.top}
              $left={menuPosition.left}
              $width={menuPosition.width}
              $openUp={menuPosition.openUp}
            >
              {searchable ? (
                <MenuSearch
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={searchPlaceholder}
                />
              ) : null}

              <MenuViewport $maxHeight={viewportMaxHeight}>
                {filteredOptions.length === 0 ? (
                  <EmptyText>{emptyLabel}</EmptyText>
                ) : (
                  filteredOptions.map((option) => (
                    <MenuOption
                      key={option.value}
                      type="button"
                      $selected={option.value === value}
                      onClick={() => {
                        onChange(option.value);
                        setOpen(false);
                        setQuery('');
                      }}
                    >
                      {option.label}
                    </MenuOption>
                  ))
                )}
              </MenuViewport>
            </Menu>,
            document.body,
          )
        : null}
    </Wrap>
  );
}
